import React from 'react'
import { XelaColor } from '@/XelaReact'
import { Box } from '@mantine/core'
import { router } from '@inertiajs/react'
import reactStringReplace from 'react-string-replace'
import moment from 'moment'
import { clone } from 'lodash'
import { updateShowDocumentEditor } from '@/Store/globalSlice'
import { useDispatch } from 'react-redux'
import { EntityFormItem, EntitySectionData } from '@/Types/EntitySubTypes'

export const getSwimlaneHeadingTextColor = (color: string): string => {
  switch (color) {
    case 'blue':
      return XelaColor.Blue3
    case 'pink':
      return XelaColor.Pink3
    case 'yellow':
      return XelaColor.Yellow3
    case 'orange':
      return XelaColor.Orange3
    case 'green':
      return XelaColor.Green3
    case 'red':
      return XelaColor.Red3
  }

  return XelaColor.Blue3
}

export const getSwimlaneHeadingBackgroundColor = (color: string): string => {
  switch (color) {
    case 'blue':
      return XelaColor.Blue12
    case 'pink':
      return XelaColor.Pink12
    case 'yellow':
      return XelaColor.Yellow12
    case 'orange':
      return XelaColor.Orange12
    case 'green':
      return XelaColor.Green12
    case 'red':
      return XelaColor.Red12
  }

  return XelaColor.Blue12
}

export const greetingTime = () => {
  const currentHour: number = (moment().format('HH') as unknown) as number

  if (currentHour >= 3 && currentHour < 12) {
    return 'Good Morning'
  } else if (currentHour >= 12 && currentHour < 15) {
    return 'Good Afternoon'
  } else if (currentHour >= 15 && currentHour < 20) {
    return 'Good Evening'
  } else if (currentHour >= 20 && currentHour < 3) {
    return 'Good Night'
  } else {
    return 'Hello'
  }
}

export const getRouteFromEntity = (entity: string, entityId: number, agentId: number | null = null) => {
  switch (entity) {
    case 'Lead':
      if (agentId != null) {
        return `/b2b/leads/${entityId}?via=leads`
      }

      return `/leads/${entityId}`
    case 'Counselling':
      return `/counsellings/${entityId}`
    case 'Admission':
      return `/admissions/${entityId}`
    case 'Visa':
      return `/visas/${entityId}`
    case 'IELTS Lead':
      return `/ielts-leads/${entityId}`
    case 'Visitor Lead':
      return `/visitor-leads/${entityId}`
  }

  return `/leads/${entityId}`
}

export const replaceVariablesForMentions = (string: string) => {
  const dispatch = useDispatch()

  let replacedText

  // Match URLs
  replacedText = reactStringReplace(string, /(https?:\/\/\S+)/g, (match, i) => (
    <a key={match + i} href={match}>
      {match}
    </a>
  ))

  // Match @mentions
  replacedText = reactStringReplace(replacedText, /(@\[[\w\s]+]\(user:\d+\))/g, (match, i) => {
    const results = /@\[([\w\s]+)]\(user:(\d+)\)/g.exec(match)

    if (results && results[1] && results[2])
      return (
        <Box
          key={match + i}
          component="span"
          style={{
            color: XelaColor.Blue3,
            marginRight: '1px',
          }}
        >
          {`@${results[1]}`}
        </Box>
      )

    return match
  })

  let leadId: string | undefined

  // Match #mentions
  replacedText = reactStringReplace(replacedText, /(#\[[\w\s]+\(\d+\)]\(lead:\d+\))/g, (match, i) => {
    const results = /#\[([\w\s]+)\((\d+)\)]\(lead:(\d+)\)/g.exec(match)

    if (results && results[1] && results[2] && results[3]) {
      leadId = clone(results[3])
      return (
        <Box
          key={match + i}
          component="span"
          style={{
            color: XelaColor.Orange3,
            cursor: 'pointer',
            marginRight: '1px',
          }}
          onClick={() => {
            router.visit(route('leads.id', { id: results[3] }))
          }}
        >
          {`#${results[1]} (${results[2]})`}
        </Box>
      )
    }

    return match
  })

  // Match !mentions
  replacedText = reactStringReplace(replacedText, /(!\[[\w\s]+]\(document:[\w\s]+\))/g, (match, i) => {
    const results = /!\[([\w\s]+)]\(document:([\w\s]+)\)/g.exec(match)

    if (results && results[1] && results[2])
      return (
        <Box
          key={match + i}
          component="span"
          style={{
            color: XelaColor.Purple3,
            cursor: 'pointer',
            marginRight: '1px',
          }}
          onClick={() => {
            dispatch(
              updateShowDocumentEditor({
                show: true,
                documentType: results[2],
                documentLeadId: leadId,
              })
            )
          }}
        >
          {`!${results[1]}`}
        </Box>
      )

    return match
  })

  return replacedText
}

export const getInitials = (firstName: string, lastName: string): string => {
  if (lastName === undefined || lastName === null || lastName === '' || lastName === '.') {
    return firstName.charAt(0).toUpperCase().slice(0, 2)
  }

  return firstName.slice(0, 1).toUpperCase() + lastName.slice(0, 1).toUpperCase()
}

export const maskPhone = (phone: string, country_calling_code: string) => {
  if (phone === undefined || phone === null || phone === '') {
    return ''
  } else {
    const hiddenPart = '*'.repeat(phone.length - 2)
    return (country_calling_code ?? '') + hiddenPart + phone.slice(-2)
  }
}

export const maskEmail = (email: string) => {
  if (email === undefined || email === null || email === '') {
    return ''
  } else {
    const [username, domain] = email.split('@')
    return '*'.repeat(username.length) + '@' + domain
  }
}

export const getOrderOfFieldByLabel = (
  fieldsData: EntitySectionData | undefined,
  section: string,
  value: string
): number => {
  const sectionData = fieldsData?.[section]

  if (sectionData) {
    const field = sectionData.find((field: EntityFormItem) => field.value === value)
    return field ? field.order : 0
  }

  return 0
}

export const getSelectedOfFieldByLabel = (
  fieldsData: EntitySectionData | undefined,
  section: string,
  value: string
): boolean => {
  const sectionData = fieldsData?.[section]

  if (sectionData) {
    const field = fieldsData?.[section].find((field: EntityFormItem) => field.value === value)
    return field ? field.selected : false
  }

  return false
}
